/**
 * Currency Utilities
 */

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  decimals: number;
  symbolPosition: 'before' | 'after';
}

export const CURRENCIES: Record<string, CurrencyInfo> = {
  TND: { code: 'TND', symbol: 'د.ت', name: 'Tunisian Dinar', decimals: 3, symbolPosition: 'after' },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', decimals: 2, symbolPosition: 'before' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', decimals: 2, symbolPosition: 'after' },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', decimals: 2, symbolPosition: 'before' },
  LYD: { code: 'LYD', symbol: 'ل.د', name: 'Libyan Dinar', decimals: 3, symbolPosition: 'after' },
  EGP: { code: 'EGP', symbol: 'ج.م', name: 'Egyptian Pound', decimals: 2, symbolPosition: 'after' },
  SAR: { code: 'SAR', symbol: 'ر.س', name: 'Saudi Riyal', decimals: 2, symbolPosition: 'after' },
  AED: { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', decimals: 2, symbolPosition: 'after' },
  MAD: { code: 'MAD', symbol: 'د.م', name: 'Moroccan Dirham', decimals: 2, symbolPosition: 'after' },
  DZD: { code: 'DZD', symbol: 'د.ج', name: 'Algerian Dinar', decimals: 2, symbolPosition: 'after' },
};

export function getCurrencyInfo(code: string): CurrencyInfo | null {
  return CURRENCIES[code] || null;
}

export function formatCurrency(amount: number, currencyCode: string): string {
  const currency = CURRENCIES[currencyCode];
  if (!currency) {
    return `${amount.toFixed(2)} ${currencyCode}`;
  }

  const formattedAmount = amount.toFixed(currency.decimals);
  
  if (currency.symbolPosition === 'before') {
    return `${currency.symbol}${formattedAmount}`;
  }
  return `${formattedAmount} ${currency.symbol}`;
}

export function getCurrencySymbol(code: string): string {
  return CURRENCIES[code]?.symbol || code;
}

export function isValidCurrency(code: string): boolean {
  return code in CURRENCIES;
}

export function getSupportedCurrencies(): CurrencyInfo[] {
  return Object.values(CURRENCIES);
}

/**
 * Round a sell price TTC to the nearest shelf step (default 0.05 TND).
 *
 * Examples (step 0.05):
 * - 7.501 → 7.5
 * - 1.54 → 1.55
 * - 1.56 → 1.55
 * - 1.59 → 1.6
 */
export function roundSalePriceTtc(amount: number, step = 0.05): number {
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  if (!(step > 0)) return amount;
  const rounded = Math.round(amount / step) * step;
  // Keep millime precision for TND (3 decimals) without float drift.
  return Math.round(rounded * 1000) / 1000;
}

/** Derive salePriceHT from a TTC amount after rounding TTC to the shelf step. */
export function salePriceHtFromRoundedTtc(
  ttc: number,
  taxRate: number,
  step = 0.05,
): number {
  const factor = 1 + (taxRate || 0);
  if (!(factor > 0)) return 0;
  const roundedTtc = roundSalePriceTtc(ttc, step);
  return Math.round((roundedTtc / factor) * 1000) / 1000;
}

/** Snap HT so that HT × (1 + tax) lands on a rounded TTC shelf price. */
export function snapSalePriceHtToRoundedTtc(
  salePriceHT: number,
  taxRate: number,
  step = 0.05,
): number {
  if (!(salePriceHT > 0)) return 0;
  const factor = 1 + (taxRate || 0);
  return salePriceHtFromRoundedTtc(salePriceHT * factor, taxRate, step);
}

/**
 * Unit sell price TTC for display / labels: HT × (1 + tax), then round to shelf step.
 * Use this anywhere PU vente TTC is shown so millime float (e.g. 7.50057 → 7.501) never appears.
 */
export function salePriceTtcFromHt(
  salePriceHT: number,
  taxRate: number,
  step = 0.05,
): number {
  if (!(salePriceHT > 0)) return 0;
  return roundSalePriceTtc(salePriceHT * (1 + (taxRate || 0)), step);
}

function roundMillimes(n: number): number {
  return Math.round(n * 1000) / 1000;
}

export interface CartPricingItem {
  quantity: number;
  /** Unit price HT (catalog). */
  unitPrice: number;
  taxRate: number;
  productDiscountPercentage?: number;
  /** Product remise amount in HT (legacy). */
  productDiscountAmount?: number;
}

export interface CartPricingTotals {
  /** Σ qty × rounded unit TTC (before remises). */
  subtotalTtc: number;
  productDiscountTtc: number;
  cartDiscountTtc: number;
  discountAmountTtc: number;
  /** TVA portion embedded in the payable TTC (informational). */
  taxAmount: number;
  /** Payable total TTC. */
  grandTotal: number;
  /** Σ qty × unitPrice HT (legacy / API line basis). */
  subtotalHt: number;
}

/**
 * Cart totals from rounded shelf TTC (not HT × (1+TVA)).
 * Ensures PU TTC 7.500 → line/subtotal/grand total 7.500, never 7.501.
 * Remise panier % applies on the total **after** remises produit.
 */
export function calcCartTotalsFromRoundedTtc(
  cart: CartPricingItem[],
  cartDiscountPercentage = 0,
): CartPricingTotals {
  let subtotalTtc = 0;
  let subtotalHt = 0;
  let productDiscountTtc = 0;

  for (const item of cart) {
    const tax = item.taxRate || 0;
    const unitTtc = salePriceTtcFromHt(item.unitPrice, tax);
    const grossTtc = item.quantity * unitTtc;
    const lineHt = item.quantity * item.unitPrice;
    subtotalTtc += grossTtc;
    subtotalHt += lineHt;

    if ((item.productDiscountPercentage || 0) > 0) {
      productDiscountTtc += grossTtc * ((item.productDiscountPercentage || 0) / 100);
    } else if ((item.productDiscountAmount || 0) > 0 && lineHt > 0) {
      productDiscountTtc += grossTtc * ((item.productDiscountAmount || 0) / lineHt);
    }
  }

  const afterProductDiscountTtc = Math.max(0, subtotalTtc - productDiscountTtc);
  const cartDiscountTtc =
    afterProductDiscountTtc * ((cartDiscountPercentage || 0) / 100);
  const discountAmountTtc = productDiscountTtc + cartDiscountTtc;
  const grandTotal = Math.max(0, subtotalTtc - discountAmountTtc);

  let taxAmount = 0;
  for (const item of cart) {
    const tax = item.taxRate || 0;
    const unitTtc = salePriceTtcFromHt(item.unitPrice, tax);
    const grossTtc = item.quantity * unitTtc;
    let productDisc = 0;
    if ((item.productDiscountPercentage || 0) > 0) {
      productDisc = grossTtc * ((item.productDiscountPercentage || 0) / 100);
    } else if ((item.productDiscountAmount || 0) > 0) {
      const lineHt = item.quantity * item.unitPrice;
      productDisc = lineHt > 0 ? grossTtc * ((item.productDiscountAmount || 0) / lineHt) : 0;
    }
    const lineNet = Math.max(0, grossTtc - productDisc);
    const lineCartDisc =
      afterProductDiscountTtc > 0
        ? (lineNet / afterProductDiscountTtc) * cartDiscountTtc
        : 0;
    const lineAfter = Math.max(0, lineNet - lineCartDisc);
    if (tax > 0) {
      taxAmount += lineAfter - lineAfter / (1 + tax);
    }
  }

  return {
    subtotalTtc: roundMillimes(subtotalTtc),
    productDiscountTtc: roundMillimes(productDiscountTtc),
    cartDiscountTtc: roundMillimes(cartDiscountTtc),
    discountAmountTtc: roundMillimes(discountAmountTtc),
    taxAmount: roundMillimes(taxAmount),
    grandTotal: roundMillimes(grandTotal),
    subtotalHt: roundMillimes(subtotalHt),
  };
}

/** Line total TTC after product remise, from rounded unit TTC. */
export function cartLineTotalTtc(item: CartPricingItem): number {
  const tax = item.taxRate || 0;
  const unitTtc = salePriceTtcFromHt(item.unitPrice, tax);
  const grossTtc = item.quantity * unitTtc;
  if ((item.productDiscountPercentage || 0) > 0) {
    return roundMillimes(grossTtc * (1 - (item.productDiscountPercentage || 0) / 100));
  }
  if ((item.productDiscountAmount || 0) > 0) {
    const lineHt = item.quantity * item.unitPrice;
    if (lineHt > 0) {
      return roundMillimes(grossTtc * (1 - (item.productDiscountAmount || 0) / lineHt));
    }
  }
  return roundMillimes(grossTtc);
}

